// import { PaginationRequest } from "src/types/commonTypes";
import { PaginationRequest } from "src/types/commonTypes";
import client from "../client";
import internalClient from "../internalClient";

export type WalletTransferRequest = {
  amount: number;
  designationWalletId: string;
  sourceWalletId: string;
  expenseId?: string;
};

export const internalWalletTransfer = (data: WalletTransferRequest) => {
  return client({
    query: `mutation WalletTransfer($input: WalletTransferInput) { 
        wallet_transfer(input: $input) { 
            destination { 
                balance 
                wallet_id 
                is_default 
                display_id
            } 
            source { 
                balance 
                display_id 
                name 
                wallet_id 
            } 
        } 
    }`,
    variables: {
      input: {
        amount: data.amount,
        destination_wallet_id: data.designationWalletId,
        source_wallet_id: data.sourceWalletId,
        expense_id: data.expenseId
        // wallet_id: data.sourceWalletId
      }
    }
  });
};

export type SinglePayRequest = {
  userId?: string;
  cardId: string;
  walletId: string;
  expireAt?: string;
  linkType?: string;
};

export const singlePay = (data: SinglePayRequest) => {
  return client({
    query: `mutation CreateCardSetting($input: CreateCardSettingInput) { 
        create_card_setting(input: $input) { 
            card_id 
            ppan 
            wallet_id 
            card_setting_status 
            card_setting_id
        } 
    }`,
    variables: {
      input: {
        user_id: data.userId,
        card_id: data.cardId,
        expires_at: data.expireAt,
        link_type: data.linkType,
        wallet_id: data.walletId
      }
    }
  });
};

export type groupPayRequest = {
  destination: {
    walletId: string;
  }[];
  source: {
    amount: string;
    walletId: string;
  }[];
};

export const groupPay = (data: groupPayRequest) => {
  return client({
    query: `mutation GroupWalletTransfer($input: GroupWalletTransferInput) { group_wallet_transfer(input: $input) { sources { balance } } } `,
    variables: {
      input: {
        destinations: data.destination.map((destination: any) => ({
          wallet_id: destination.walletId
        })),
        sources: data.source.map((source: any) => ({
          amount: source.amount,
          wallet_id: source.walletId
        }))
      }
    }
  });
};

export interface SearchOrderRequest extends PaginationRequest {
  filter?: {
    order_filter?: {
      object_id?: string;
      order_by?: string;
      organisation_id?: string;
      order_id_list?: string[];
      order_status?: string;
      entitlement_id?: string;
    };
  };
}

export const searchOrder = (data: SearchOrderRequest) => {
  return client({
    query: `query SearchOrderList($input: SearchInput) {
      search_order(input: $input) {
        total_pages
        total_size
        items {
          user {
            attributes {
              first_name
              last_name
            }
            display_id
            email
            user_id
            username
          }
          order_id
          amount
          display_id
          status
          organisation_id
        }
      }
    }`,
    variables: {
      input: data
    }
  });
};

export const searchInternalOrder = (data: SearchOrderRequest) => {
  return internalClient({
    query: `query SearchInternalOrderList($input: SearchInput) {
      internal_search_order(input: $input) {
        total_pages
        total_size
        items {
          user {
            attributes {
              first_name
              last_name
            }
            display_id
            email
            user_id
            username
          }
          order_id
          amount
          display_id
          status
          organisation_id
        }
      }
    }`,
    variables: {
      input: data
    }
  });
};

export const getOrder = (orderId: string) => {
  return client({
    query: `query GetOrder($input: GetOrderInput) {
      get_order(input: $input) {
        user {
          attributes {
            first_name
            last_name
          }
          display_id
          email
          user_id
          username
        }
        order_id
        amount
        display_id
        status
        organisation_id
        items {
          order_item_id
          object_id
          amount
          order_item_type
        }
      }
    }`,
    variables: {
      input: {
        order_id: orderId
      }
    }
  });
};

export const getPayments = (orderId: string) => {
  return client({
    query: `query GetPayments($input: GetPaymentsInput) {
      get_payments(input: $input) {
        payment_id
        order_id
        amount
        payment_status
        provider_payment_id
        display_id
        organisation_id
        order_by
      }
    }`,
    variables: {
      input: {
        order_id: orderId
      }
    }
  });
};

export type CreateReimbursementRequest = {
  source_wallet_id: string;
  destination_wallet_id?: string;
  merchant_name: string;
  amount: number;
  description?: string;
  for_user_ids: string[];
  transaction_date: string;
};

export const createReimbursement = (data: CreateReimbursementRequest) => {
  return client({
    query: `mutation CreateReimbursement($input: CreateReimbursementInput) {
      create_reimbursement(input: $input) {
        reimbursement_id
      }
    }`,
    variables: {
      input: data
    }
  });
};

export type UpdateReimbursementRequest = {
  reimbursement_id: string;
  amount: number;
  description?: string;
  merchant_name: string;
  for_user_ids: string[];
};

export const updateReimbursement = (data: UpdateReimbursementRequest) => {
  return client({
    query: `mutation UpdateReimbursement($input: UpdateReimbursementInput) {
      update_reimbursement(input: $input) {
        reimbursement_id
      }
    }`,
    variables: {
      input: data
    }
  });
};

export type UpdateReimbursementStatusRequest = {
  reimbursement_id: string;
  status: string;
  source_wallet_id?: string;
  destination_wallet_id?: string;
  rejection_reason?: string;
  approved_amount?: number;
};

export const updateReimbursementStatus = (data: UpdateReimbursementStatusRequest) => {
  return client({
    query: `mutation UpdateReimbursementStatus($input: UpdateReimbursementStatusInput) {
      update_reimbursement_status(input: $input) {
        reimbursement_id
      }
    }`,
    variables: {
      input: data
    }
  });
};

export const retryReimbursementSettlement = (reimbursementId: string) => {
  return client({
    query: `mutation RetryReimbursementSettlement($input: RetryReimbursementSettlementInput) {
      retry_reimbursement_settlement(input: $input) {
        reimbursement_id
      }
    }`,
    variables: {
      input: {
        reimbursement_id: reimbursementId
      }
    }
  });
};

export interface GetReimbursementListRequest extends PaginationRequest {
  filter?: {
    reimbursement_filter?: {
      reimbursement_ids: string[];
      organisation_id: string;
      statuses: string[];
      status: string;
      requested_by: string;
      source_wallet_id: string;
      destination_wallet_id: string;
      participant_user_id: string;
      display_id: string;
      current_wtr_id: string;
      merchant_name: string;
      query: string;
      created_at: Range;
    };
  };
}

export const getReimbursementList = (data: GetReimbursementListRequest) => {
  return client({
    query: `query GetReimbursementList($input: SearchInput) {
      search_reimbursement(input: $input) {
        total_pages
        total_size
        items {
          reimbursement_id
	        organisation_id
	        source_wallet_id
	        destination_wallet_id
	        amount
	        approved_amount
	        description
	        merchant_name
	        transaction_date
	        requested_by
	        for_user_ids
	        status
	        current_wtr_id
	        display_id
	        created_at
	        updated_at
	        updated_by
	        submitted_at
	        approved_at
	        settled_at
	        completed_at
	        approved_by
	        rejection_reason
          source_wallet {
            wallet_id
            name
            balance
            wallet_type
            user {
              user_id
              email
              attributes {
                first_name
                last_name
              }
            }
            group {
              group_id
              name
              group_type
            }
          }
          destination_wallet {
            wallet_id
            name
            balance
            wallet_type
            user {
              user_id
              email
              attributes {
                first_name
                last_name
              }
            }
            group {
              group_id
              name
              group_type
            }
          }
          requested_by_user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
          updated_by_user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
          approved_by_user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
        }
      }
    }`,
    variables: {
      input: data
    }
  });
};

export const getReimbursement = (reimbursementId: string) => {
  return client({
    query: `query GetReimbursement($input: GetReimbursementInput) {
      get_reimbursement(input: $input) {
        reimbursement_id
        organisation_id
        source_wallet_id
        destination_wallet_id
        amount
        approved_amount
        description
        merchant_name
        transaction_date
        requested_by
        for_user_ids
        status
        current_wtr_id
        display_id
        created_at
        updated_at
        updated_by
        submitted_at
        approved_at
        settled_at
        completed_at
        approved_by
        rejection_reason
        source_wallet {
          wallet_id
          name
          balance
          wallet_type
          user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
        destination_wallet {
          wallet_id
          name
          balance
          wallet_type
          user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
        requested_by_user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
        updated_by_user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
        approved_by_user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }`,
    variables: {
      input: { reimbursement_id: reimbursementId }
    }
  });
};

export const getWtr = (wtrId: string) => {
  return client({
    query: `query GetWtr($input: GetWtrInput) {
      get_wtr(input: $input) {
        wtr_id
        organisation_id
        operation_type
        source_wallet_id
        destination_wallet_id
        amount
        execution_mode
        status
        approver_role_required
        scheduled_at
        purpose
        context
        requested_by
        display_id
        updated_by
        created_at
        updated_at
        failure_reason
        source_wallet {
          wallet_id
          name
          balance
          wallet_type
          user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
        destination_wallet {
          wallet_id
          name
          balance
          wallet_type
          user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
        requested_by_user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
        updated_by_user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }`,
    variables: { input: { wtr_id: wtrId } }
  });
};

export const getReimbursementHistory = (reimbursementId: string) => {
  return client({
    query: `query GetReimbursementHistory($input: GetReimbursementHistoryInput) {
      get_reimbursement_history(input: $input) {
        audit_id
        created_at
        to_status
        actor_id
        payload
        op
      }
    }`,
    variables: { input: { reimbursement_id: reimbursementId } }
  });
};

export const getReimbursementEligibleSourceWallets = (reimbursementId: string) => {
  return client({
    query: `query GetReimbursementEligibleSourceWallets($input: GetReimbursementEligibleSourceWalletsInput) {
      get_reimbursement_eligible_source_wallets(input: $input) {
        wallets {
          wallet_id
          name
          balance
          wallet_type
          user {
            user_id
            email
            attributes {
              first_name
              last_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
      }
    }`,
    variables: { input: { reimbursement_id: reimbursementId } }
  });
};
